import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { Empty, Spin, Tag } from 'antd';
import {
  useClaimArtifactsHook,
  usePolicyClaimsHook,
} from '../../data/policyClaims/usePolicyClaimsHook';
import useSchemaHook from '../../data/schemas/useSchemaHook';
import DocumentView from '../ArtifactsTab/DocumentView';
import styles from './ClaimsTab.module.scss';

const CLAIM_SCHEMA_NAME = 'claim_overview';
const CLOSED_STATUSES = new Set(['Closed', 'Denied']);

interface Props {
  claimId: string;
}

const ClaimOverviewDetail: React.FC<Props> = ({ claimId }) => {
  const intl = useIntl();
  const { dossierId } = useParams<{ dossierId: string }>();

  const [{ claims, hasData: hasClaims, status: claimsStatus }, claimsHandler] =
    usePolicyClaimsHook(dossierId ?? '');
  const claimsDispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasClaims || claimsDispatchedRef.current === dossierId) {
      return;
    }
    claimsDispatchedRef.current = dossierId;
    claimsHandler.fetchClaims();
  }, [dossierId, hasClaims, claimsHandler]);

  const [
    { artifacts, hasData: hasArtifacts, status: artifactsStatus },
    artifactsHandler,
  ] = useClaimArtifactsHook(claimId);
  const artifactsDispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      !claimId ||
      hasArtifacts ||
      artifactsDispatchedRef.current === claimId
    ) {
      return;
    }
    artifactsDispatchedRef.current = claimId;
    artifactsHandler.fetchClaimArtifacts();
  }, [claimId, hasArtifacts, artifactsHandler]);

  const [{ bundle, hasSchema, status: schemaStatus }, schemaHandler] =
    useSchemaHook(CLAIM_SCHEMA_NAME);
  const schemaDispatchedRef = React.useRef(false);
  React.useEffect(() => {
    if (hasSchema || schemaDispatchedRef.current) {
      return;
    }
    schemaDispatchedRef.current = true;
    schemaHandler.fetchSchema();
  }, [hasSchema, schemaHandler]);

  const claim = claims.find((c) => c.claim_id === claimId);
  const overview = artifacts.find((a) => a.schema_name === CLAIM_SCHEMA_NAME);
  const title = claim
    ? [claim.number, claim.caption].filter(Boolean).join(' — ') || 'Claim'
    : 'Claim';

  const claimNotInDossier = hasClaims && !claim;
  const ready = hasClaims && hasArtifacts && bundle && !!claim;
  const failed =
    claimNotInDossier ||
    claimsStatus === 'rejected' ||
    artifactsStatus === 'rejected' ||
    schemaStatus === 'rejected';

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.titleBlock}>
          <h1 className={styles.claimTitle}>{title}</h1>
          {claim && (
            <div className={styles.statusRow}>
              <Tag
                className={styles.statusTag}
                color={CLOSED_STATUSES.has(claim.status) ? 'default' : 'blue'}
              >
                {claim.status}
              </Tag>
              {claim.created_at && (
                <span className={styles.createdTime}>
                  Created{' '}
                  {intl.formatDate(claim.created_at, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
          )}
        </div>
        {!ready && !failed ? (
          <div className={styles.loading}>
            <Spin />
          </div>
        ) : claim && overview && bundle?.ui_schema ? (
          <DocumentView
            uiSchema={bundle.ui_schema as Record<string, any>}
            data={overview.data}
            variant="flat"
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>
    </div>
  );
};

export default ClaimOverviewDetail;
